import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import {
  calculateWpm,
  calculateAccuracy,
  formatDuration,
  getCharStatus,
  isLessonComplete,
} from './typingTutorUtils';
import { pickRacerPassage } from './typingTutorGames';

/**
 * Race a ghost moving at target WPM — finish the passage ahead to win
 */
export default function TypeRacerGame({
  targetWpm = 35,
  onComplete,
  onRetry,
  onCancel,
}) {
  const passage = useMemo(() => pickRacerPassage(), []);
  const target = passage;

  const [typed, setTyped] = useState('');
  const [started, setStarted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const reset = useCallback(() => {
    setTyped('');
    setStarted(false);
    setElapsed(0);
    setFinished(false);
    setResult(null);
    startTimeRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onRetry?.();
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [onRetry]);

  useEffect(() => {
    reset();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [reset]);

  const finishGame = useCallback(
    (finalTyped) => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      const timeSeconds = Math.max(
        1,
        startTimeRef.current
          ? Math.floor((Date.now() - startTimeRef.current) / 1000)
          : elapsed || 1,
      );
      let correct = 0;
      let errors = 0;
      for (let i = 0; i < finalTyped.length; i += 1) {
        if (finalTyped[i] === target[i]) correct += 1;
        else errors += 1;
      }
      const wpm = calculateWpm(correct, timeSeconds);
      const accuracy = calculateAccuracy(correct, finalTyped.length);
      const ghostAtFinish = Math.floor((timeSeconds / 60) * targetWpm * 5);
      const won = finalTyped.length >= target.length && ghostAtFinish < target.length;

      const sessionResult = {
        wpm,
        accuracy,
        timeSpentSeconds: timeSeconds,
        errors,
        totalChars: finalTyped.length,
        correctChars: correct,
        goalMet: wpm >= targetWpm,
        won,
        ghostWpm: targetWpm,
      };
      setResult(sessionResult);
      setFinished(true);
      onComplete?.(sessionResult);
    },
    [elapsed, target, targetWpm, onComplete],
  );

  useEffect(() => {
    const handleKey = (e) => {
      if (finished) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'Escape') {
        onCancel?.();
        return;
      }
      if (e.key === 'Backspace') {
        e.preventDefault();
        setTyped((prev) => prev.slice(0, -1));
        return;
      }
      if (e.key.length !== 1) return;
      e.preventDefault();

      if (!started) {
        setStarted(true);
        startTimeRef.current = Date.now();
        timerRef.current = setInterval(() => {
          setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }, 250);
      }

      setTyped((prev) => {
        if (prev.length >= target.length) return prev;
        const next = prev + e.key;
        if (isLessonComplete(next, target)) {
          setTimeout(() => finishGame(next), 0);
        }
        return next;
      });
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [finished, started, target, finishGame, onCancel]);

  const correctCount = typed.split('').filter((ch, i) => ch === target[i]).length;
  const liveWpm = started ? calculateWpm(correctCount, Math.max(1, elapsed)) : 0;
  const userPct = Math.min(100, (typed.length / Math.max(target.length, 1)) * 100);

  const ghostChars = started
    ? Math.min(
        target.length,
        Math.floor((elapsed / 60) * targetWpm * 5),
      )
    : 0;
  const ghostPct = Math.min(100, (ghostChars / Math.max(target.length, 1)) * 100);

  const renderChars = () =>
    target.split('').map((char, index) => {
      const status = getCharStatus(typed, target, index);
      const isCurrent = index === typed.length && !finished;
      let className = 'tt-char-pending';
      if (status === 'correct') className = 'tt-char-correct';
      if (status === 'incorrect') className = 'tt-char-incorrect';
      if (isCurrent) className += ' tt-char-current';
      return (
        <span key={index} className={className}>
          {char}
        </span>
      );
    });

  if (finished && result) {
    return (
      <Box className="tt-result-panel tt-card">
        {result.won && (
          <EmojiEventsIcon sx={{ fontSize: 48, color: '#f39c12', mb: 1 }} />
        )}
        <Typography variant="h5" className="tt-sans" fontWeight={700} gutterBottom>
          {result.won ? 'You Beat the Ghost!' : 'Race Complete'}
        </Typography>
        <Box className="tt-stats-bar" sx={{ justifyContent: 'center', mb: 2, mt: 2 }}>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.wpm}</div>
            <div className="tt-stat-label">Your WPM</div>
          </div>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.ghostWpm}</div>
            <div className="tt-stat-label">Ghost WPM</div>
          </div>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{result.accuracy}%</div>
            <div className="tt-stat-label">Accuracy</div>
          </div>
          <div className="tt-stat-item">
            <div className="tt-stat-value">{formatDuration(result.timeSpentSeconds)}</div>
            <div className="tt-stat-label">Time</div>
          </div>
        </Box>
        <Typography className="tt-sans" sx={{ color: result.goalMet ? 'success.main' : 'text.secondary', mb: 2 }}>
          {result.won
            ? `You outran the ${targetWpm} WPM ghost!`
            : result.goalMet
              ? `Good speed — try to finish ahead of the ghost next time.`
              : `Ghost runs at ${targetWpm} WPM — keep practicing.`}
        </Typography>
        <Button variant="outlined" startIcon={<ReplayIcon />} onClick={reset} sx={{ mr: 1 }}>
          Race Again
        </Button>
        <Button variant="contained" color="success" onClick={onCancel}>
          Back to Games
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box className="tt-racer-track" sx={{ mb: 3 }}>
        <Box className="tt-racer-lane">
          <span className="tt-racer-label tt-sans">You</span>
          <div className="tt-racer-bar">
            <div className="tt-racer-car tt-racer-you" style={{ left: `${userPct}%` }}>🏎️</div>
          </div>
        </Box>
        <Box className="tt-racer-lane">
          <span className="tt-racer-label tt-sans">Ghost ({targetWpm} WPM)</span>
          <div className="tt-racer-bar">
            <div className="tt-racer-car tt-racer-ghost" style={{ left: `${ghostPct}%` }}>👻</div>
          </div>
        </Box>
      </Box>

      <Box className="tt-stats-bar" sx={{ mb: 2 }}>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{liveWpm}</div>
          <div className="tt-stat-label">WPM</div>
        </div>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{Math.round(userPct)}%</div>
          <div className="tt-stat-label">Progress</div>
        </div>
        <div className="tt-stat-item">
          <div className="tt-stat-value">{formatDuration(elapsed)}</div>
          <div className="tt-stat-label">Time</div>
        </div>
      </Box>

      <div
        className="tt-typing-area tt-mono"
        onClick={() => inputRef.current?.focus()}
        role="textbox"
        tabIndex={0}
      >
        {renderChars()}
      </div>

      <input ref={inputRef} className="tt-hidden-input" autoFocus aria-hidden tabIndex={-1} />

      <Typography className="tt-keyboard-hint tt-sans">
        {!started
          ? 'Start typing to race the ghost. Finish the passage before the ghost catches up!'
          : 'Stay ahead of the ghost. Esc to exit.'}
      </Typography>
    </Box>
  );
}
